/**
 * FilteredListPageLayout — the "filter UI + results list" page template
 * (Issues, Pull requests, Projects index, etc.).
 *
 * Composes `SidebarPageLayout` so there's a single source of truth for
 * the page chrome (sidebar + collapsible rail + narrow-viewport dialog +
 * header + footer). On top of that shell this template adds three
 * landmark slots specific to filtered-list pages:
 *
 * - {@link FilterBar} — `<search>` landmark for the filter bar
 * - {@link Results} — `<section>` landmark for the results
 * - {@link HeaderActions} — responsive primary/secondary action row that
 *   collapses secondary actions into a kebab `ActionMenu` on narrow
 *   viewports
 *
 * `Header`, `Sidebar`, `Content`, `Footer`, and `SidebarTrigger` are
 * re-exports of `SidebarPageLayout.*` so consumers have a single import
 * surface. If you need the generic shell without filter slots, drop down
 * to `SidebarPageLayout` directly — there is no behavior drift between
 * the two.
 *
 * The {@link HeaderActions} slot is the only sub-component with real
 * behavior; consumers who need fully custom markup can drop down to the
 * Layer 0 hook {@link useHeaderActionsOverflow} for the same responsive
 * contract. See `core-ux#2238` for the layered model.
 */
import {KebabHorizontalIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, type PageLayoutProps} from '@primer/react'
import {clsx} from 'clsx'
import {Fragment, type ComponentPropsWithoutRef, type PropsWithChildren, type ReactNode, type RefObject} from 'react'

import {
  SidebarPageLayout,
  SidebarPageLayoutContent as BaseContent,
  SidebarPageLayoutFooter as BaseFooter,
  SidebarPageLayoutHeader as BaseHeader,
  SidebarPageLayoutPageHeader as BasePageHeader,
  SidebarPageLayoutSidebar as BaseSidebar,
  SidebarPageLayoutSidebarTrigger as BaseSidebarTrigger,
  type SidebarPageLayoutContentProps,
  type SidebarPageLayoutFooterProps,
  type SidebarPageLayoutHeaderProps,
  type SidebarPageLayoutProps,
  type SidebarPageLayoutSidebarProps,
  type SidebarPageLayoutSidebarTriggerProps,
} from '../SidebarPageLayout/SidebarPageLayout'
import styles from './FilteredListPageLayout.module.css'
import {
  useHeaderActionsOverflow,
  type HeaderAction,
  type UseHeaderActionsOverflowArgs,
} from './use-header-actions-overflow'

// ---------------------------------------------------------------------------
// Root

export type FilteredListPageLayoutProps = Pick<PageLayoutProps, 'className' | 'style'> & SidebarPageLayoutProps

/**
 * Root of the filtered-list-page-layout compound. Wraps a `SidebarPageLayout`
 * so every consumer page (Issues, Pull requests, Projects index, etc.) gets
 * the same sidebar + responsive shell, plus the filter / results / responsive
 * action slots layered on top.
 *
 * See the `FilteredListPageLayout` stories in Storybook for canonical
 * compositions.
 */
function Root(props: PropsWithChildren<FilteredListPageLayoutProps>) {
  return <SidebarPageLayout {...props} />
}

Root.displayName = 'FilteredListPageLayout'

// ---------------------------------------------------------------------------
// FilterBar — <search> landmark + standardized spacing

export type FilteredListPageLayoutFilterBarProps = {
  /** Accessible label for the landmark. Required when the bar has no labelling heading. */
  'aria-label'?: string
  /** Accessible label by id reference. Use either this or `aria-label`. */
  'aria-labelledby'?: string
  className?: string
  children?: ReactNode
}

/**
 * `<search>` landmark for the filter row. Provides standard vertical rhythm
 * between itself and the {@link Results} that follow.
 *
 * Place inside {@link Content}. Supply an `aria-label` describing what's
 * being filtered ("Filter issues", "Filter pull requests"); the landmark
 * announces it to AT users so they can jump straight to the filter input.
 */
function FilterBar({className, children, ...rest}: FilteredListPageLayoutFilterBarProps) {
  return (
    <search className={clsx(styles.FilterBar, className)} {...rest}>
      {children}
    </search>
  )
}

FilterBar.displayName = 'FilteredListPageLayout.FilterBar'

// ---------------------------------------------------------------------------
// Results — <section> landmark for the filtered list

export type FilteredListPageLayoutResultsProps = ComponentPropsWithoutRef<'section'>

/**
 * `<section>` landmark for the filtered list. Pair with {@link FilterBar} —
 * the FilterBar's bottom margin provides the visual separation; this
 * component is intentionally minimal so consumers can drop any list /
 * table / grid layout inside.
 */
function Results({className, children, ...rest}: FilteredListPageLayoutResultsProps) {
  return (
    <section className={className} {...rest}>
      {children}
    </section>
  )
}

Results.displayName = 'FilteredListPageLayout.Results'

// ---------------------------------------------------------------------------
// HeaderActions — responsive primary/secondary action row

export type FilteredListPageLayoutHeaderActionsProps = {
  /**
   * Ordered list of actions. Order is preserved in both the inline render
   * and the overflow menu.
   *
   * Each entry can use the shorthand
   * `{key, label, primary?, leadingVisual?, onClick?, href?}` shape or the
   * advanced `{key, renderInline, renderMenuItem, alwaysInline?}` shape —
   * mix and match per action.
   */
  actions: HeaderAction[]
  /** Accessible label for the overflow kebab button. Defaults to `'More actions'`. */
  menuAriaLabel?: string
  /**
   * Viewport at and below which secondary (non-`alwaysInline`) actions move
   * into the overflow menu. Defaults to `'narrow'`.
   */
  collapseAt?: UseHeaderActionsOverflowArgs['collapseAt']
  /**
   * Ref pointed at the overflow kebab `IconButton`. Use this when an action's
   * menu item opens an anchored overlay (e.g. a `SelectPanel`) so the overlay
   * can anchor to — and return focus to — the kebab button after the menu item
   * detaches on select. The button stays mounted while the menu closes, so it's
   * the correct focus-return target for menu-driven overlays.
   *
   * Wired through the underlying `ActionMenu`'s `anchorRef`, so it resolves to
   * the rendered kebab button element.
   */
  overflowTriggerRef?: RefObject<HTMLButtonElement | null>
  className?: string
}

/**
 * Responsive header action row. Renders each action inline above the
 * collapse breakpoint; below it, non-`alwaysInline` actions move into a
 * single kebab `ActionMenu`. Place inside `<PageHeader.Actions>`.
 *
 * @example Shorthand (covers the common case — button + click handler).
 * ```tsx
 * <FilteredListPageLayout.HeaderActions
 *   menuAriaLabel="More issue actions"
 *   actions={[
 *     {key: 'new-issue', label: 'New issue', primary: true, onClick: handleNew},
 *     {key: 'labels', label: 'Labels', leadingVisual: TagIcon, href: '/labels'},
 *   ]}
 * />
 * ```
 *
 * @example Advanced — supply your own renderers for full markup control.
 * ```tsx
 * <FilteredListPageLayout.HeaderActions
 *   actions={[{
 *     key: 'notifications',
 *     alwaysInline: true,
 *     renderInline: () => <NotificationsPicker />,
 *     renderMenuItem: () => <ActionList.Item>Notifications</ActionList.Item>,
 *   }]}
 * />
 * ```
 */
function HeaderActions({
  actions,
  menuAriaLabel = 'More actions',
  collapseAt = 'narrow',
  overflowTriggerRef,
  className,
}: FilteredListPageLayoutHeaderActionsProps) {
  const {inlineActions, overflowActions} = useHeaderActionsOverflow({
    actions,
    collapseAt,
  })

  // Pick the right visibility helper based on the collapse breakpoint.
  // `collapseAt='narrow'`  → inline at regular + wide, overflow at narrow only.
  // `collapseAt='regular'` → inline at wide only,      overflow at narrow + regular.
  const inlineVisibilityClass = collapseAt === 'regular' ? styles.HiddenBelowWide : styles.HiddenAtNarrow
  const overflowVisibilityClass = collapseAt === 'regular' ? styles.HiddenAtWide : styles.HiddenAboveNarrow

  return (
    <>
      {inlineActions.map(action =>
        action.alwaysInline === true ? (
          <Fragment key={action.key}>{action.renderInline()}</Fragment>
        ) : (
          // Wrap each collapsible action individually so DOM order matches
          // the input `actions` array — splitting into "alwaysInline first,
          // collapsible after" would move the primary CTA off the right edge
          // whenever a secondary action was listed after it. The wrapper uses
          // `display: contents` while visible so the inner button still acts
          // as a direct flex child of `PageHeader.Actions`.
          <span key={action.key} className={clsx(inlineVisibilityClass, className)}>
            {action.renderInline()}
          </span>
        ),
      )}
      {overflowActions.length > 0 ? (
        <span className={clsx(overflowVisibilityClass, className)}>
          <ActionMenu anchorRef={overflowTriggerRef}>
            <ActionMenu.Anchor>
              <IconButton icon={KebabHorizontalIcon} aria-label={menuAriaLabel} variant="invisible" />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay align="end">
              <ActionList>
                {overflowActions.map(action => (
                  <Fragment key={action.key}>{action.renderMenuItem()}</Fragment>
                ))}
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        </span>
      ) : null}
    </>
  )
}

HeaderActions.displayName = 'FilteredListPageLayout.HeaderActions'

// ---------------------------------------------------------------------------
// Re-exported slots from SidebarPageLayout
//
// `Header`, `Sidebar`, `Content`, `Footer`, and `SidebarTrigger` are exposed
// directly as the SidebarPageLayout members of the same name — no wrapper.
// Wrapping breaks PageLayout's slot resolution: `useSlots` matches children
// against the registered slot config by reference equality (or `__SLOT__`
// symbol on the child type). A wrapper component is neither the registered
// slot reference nor carries the slot symbol, so it falls into `rest` and
// renders inside the content area instead of being hoisted into the
// dedicated header/footer slot positions. Direct re-export keeps the slot
// hoisting intact and removes a layer of indirection that added no value.

export type FilteredListPageLayoutHeaderProps = SidebarPageLayoutHeaderProps
export type FilteredListPageLayoutSidebarProps = SidebarPageLayoutSidebarProps
export type FilteredListPageLayoutContentProps = SidebarPageLayoutContentProps
export type FilteredListPageLayoutFooterProps = SidebarPageLayoutFooterProps
export type FilteredListPageLayoutSidebarTriggerProps = SidebarPageLayoutSidebarTriggerProps

// Named const aliases preserve the `FilteredListPageLayout*` value identifiers
// MDX docs reference via `<ArgTypes of={FilteredListPageLayoutHeader} />`. They
// resolve to the exact same component refs registered in the compound below,
// so slot resolution still matches by reference equality.
export const FilteredListPageLayoutHeader = BaseHeader
export const FilteredListPageLayoutPageHeader = BasePageHeader
export const FilteredListPageLayoutSidebar = BaseSidebar
export const FilteredListPageLayoutContent = BaseContent
export const FilteredListPageLayoutFooter = BaseFooter
export const FilteredListPageLayoutSidebarTrigger = BaseSidebarTrigger

// ---------------------------------------------------------------------------
// Compound export

export const FilteredListPageLayout = Object.assign(Root, {
  Header: FilteredListPageLayoutHeader,
  PageHeader: FilteredListPageLayoutPageHeader,
  Sidebar: FilteredListPageLayoutSidebar,
  SidebarTrigger: FilteredListPageLayoutSidebarTrigger,
  Content: FilteredListPageLayoutContent,
  FilterBar,
  Results,
  HeaderActions,
  Footer: FilteredListPageLayoutFooter,
})

// Named exports so docgen can extract prop types when referenced via
// `<ArgTypes of={...} />` in MDX docs.
export {Root as FilteredListPageLayoutRoot}
export {FilterBar as FilteredListPageLayoutFilterBar}
export {Results as FilteredListPageLayoutResults}
export {HeaderActions as FilteredListPageLayoutHeaderActions}
